import * as React from 'react';
import { SxProps, Theme } from '@mui/material';

import { Popper, PopperProps } from '../Popper';
import { List } from '../List';
import { ListItem } from '../ListItem';
import { Typography } from '../Typography';
import { Button, ButtonProps } from '../Button';

type PopperPosition = 'bottom-end' | 'bottom-start';

export interface ComboButtonMenuItem {
  disabled?: boolean;
  itemAction?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  value: string;
}

export interface ComboButtonProps {
  disableRipple?: boolean;
  buttonProps?: ButtonProps;
  menuDataProp?: string;
  menuItems: ComboButtonMenuItem[];
  popperPosition?: PopperPosition;
  text?: string;
  sxPopper?: SxProps<Theme>;
}

const comboButtonClassNames = {
  popper: 'popper',
  menuPaper: 'menuPaper',
  listRoot: 'listRoot',
  listItem: 'listItem'
};

const styles: Record<string, SxProps<Theme>> = {
  [comboButtonClassNames.popper]: {
    zIndex: 'modal'
  },
  [comboButtonClassNames.menuPaper]: {
    padding: 1,
    boxShadow: 2
  },
  [comboButtonClassNames.listRoot]: {
    backgroundColor: '#f00',
    display: 'flex',
    flexDirection: 'column',
    padding: '0'
  },
  [comboButtonClassNames.listItem]: {
    justifyContent: 'flex-start',
    width: 'auto',
    padding: '8px 0'
  }
};

const keyListeners = {
  tab: 'Tab',
  escape: 'Escape'
};

const getEnabledItemIndexes = (menuItems: ComboButtonMenuItem[]) => {
  const firstIndex = menuItems.findIndex(menuItem => !menuItem.disabled);
  const lastIndex = menuItems.reduce((enabledMatch, menuItem, index) => {
    return menuItem.disabled ? enabledMatch : index;
  }, -1);

  return [firstIndex, lastIndex];
};

const ComboButton: React.FunctionComponent<ComboButtonProps> = ({ text, buttonProps, menuItems, menuDataProp, popperPosition, sxPopper }) => {
  const [open, setOpen] = React.useState(false);
  const menuListRef = React.useRef<HTMLUListElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [firstEnabledItemIndex, lastEnabledItemIndex] = getEnabledItemIndexes(menuItems);

  const mergedButtonProps: ButtonProps = {
    ...buttonProps,
    color: 'primary',
    icon: open ? 'ExpandLess' : 'ExpandMore',
    onClick: () => setOpen(previousOpen => !previousOpen)
  };

  const handleClickAway = () => setOpen(false);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { tab, escape } = keyListeners;
    const { key, shiftKey } = event;
    const atBeginning = index === firstEnabledItemIndex;
    const atEnd = index === lastEnabledItemIndex;

    if (key === tab && menuListRef.current) {
      // Pressing shift + tab on the first item cycles focus to the last item
      if (shiftKey && atBeginning) {
        event.preventDefault();
        (menuListRef.current.children[lastEnabledItemIndex] as HTMLElement).focus();
      }

      // Pressing tab on the last item cycles focus to the first item
      if (!shiftKey && atEnd) {
        event.preventDefault();
        (menuListRef.current.children[firstEnabledItemIndex] as HTMLElement).focus();
      }
    }

    if (key === escape) {
      setOpen(false);
      if (buttonRef.current) {
        buttonRef.current.focus();
      }
    }
  };

  const handleBlur = (event: React.FocusEvent) => event.preventDefault();

  const menu = (
    <List data-selector={menuDataProp} component="nav" sx={styles[comboButtonClassNames.listRoot]} ref={menuListRef}>
      {menuItems.map(({ value, itemAction, disabled }: ComboButtonMenuItem, index: number) => {
        const key = `CBKey:${index}`;
        return (
          <ListItem
            disableRipple
            button
            alignItems="center"
            aria-label={value}
            onClick={itemAction}
            key={key}
            sx={styles[comboButtonClassNames.listItem] as any}
            disabled={disabled}
            onBlur={handleBlur}
            onKeyDown={(event: React.KeyboardEvent) => handleKeyDown(event, index)}
          >
            <Typography variant="body1">{value}</Typography>
          </ListItem>
        );
      })}
    </List>
  );

  const trigger = (
    <Button ref={buttonRef} {...mergedButtonProps}>
      {text}
    </Button>
  );

  const popperProps: PopperProps = {
    handleClickAway,
    menu,
    sxPaper: comboButtonClassNames.menuPaper as SxProps<Theme>,
    open,
    popperAnchorRef: buttonRef,
    sx: [comboButtonClassNames.popper, sxPopper] as SxProps<Theme>,
    popperPosition,
    trigger
  };

  return <Popper {...popperProps} />;
};

ComboButton.defaultProps = {
  popperPosition: 'bottom-end'
};
ComboButton.displayName = 'ComboButton';

export { ComboButton };

// ADA
// Tab (while the last list item is focused) - Moves the focus to the first list item
// Shift + Tab (while the first list item is focused) - Moves the focus to the last list item
// Esc (while any list item is focused) - Closes the menu and moves the focus to the button
